import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth, API } from "../App";
import { getToken, getAuthHeaders } from "../lib/auth";
import {
  Shield, FileText, ArrowRight, LogOut, BarChart3, Zap, Users, Globe,
  Clock, CheckCircle2, Weight, Brain, Truck, CreditCard, Wrench, Cpu,
  BookOpen, Lightbulb, Rocket, PlayCircle, Download, Lock, Sparkles, AlertTriangle
} from "lucide-react";
import PresentationVideo from "../components/PresentationVideo";
import OnboardingGuide from "../components/OnboardingGuide";
import axios from "axios";

const STOCK_FEATURES = [
  { icon: Shield, label: "Destructions sécurisées" },
  { icon: BarChart3, label: "Analyses & KPIs" },
  { icon: Weight, label: "Tonnage & Attestations" },
  { icon: Brain, label: "IA Prédictive" },
];

const LOG_FEATURES = [
  { icon: FileText, label: "Facturation assurances" },
  { icon: CreditCard, label: "Recouvrement & Créances" },
  { icon: Truck, label: "Suivi agents commerciaux & GPS" },
  { icon: CheckCircle2, label: "Bordereaux & Rapports IA" },
];

const TECH_FEATURES = [
  { icon: Wrench, label: "Copilot IA Pharmacien" },
  { icon: Cpu, label: "Google du Médicament" },
  { icon: BookOpen, label: "Profil Patient Virtuel" },
  { icon: Lightbulb, label: "Pharmacovigilance IA" },
];

export default function HubPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showVideo, setShowVideo] = useState(false);
  const [myPlan, setMyPlan] = useState(null);

  useEffect(() => {
    if (user?.role === "coursier" || user?.role === "agent commercial") navigate("/coursier", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === "pharmacy" || user?.role === "etablissement") {
      axios.get(`${API}/subscription-plans/my-plan`, { headers: getAuthHeaders() })
        .then(res => setMyPlan(res.data))
        .catch(() => {});
    }
  }, [user]);

  // Module access based on subscription plan
  const PLAN_MODULES = {
    "intel-stock": ["pharma"],
    "cmes-log-basique": ["log"],
    "cmes-log-pro": ["log"],
    "tech-conseil": ["techconseil"],
    "log-basique-stock": ["log", "pharma"],
    "log-pro-stock": ["log", "pharma"],
    "log-basique-tech": ["log", "techconseil"],
    "log-pro-tech": ["log", "techconseil"],
    "stock-tech": ["pharma", "techconseil"],
    "excellence": ["log", "pharma", "techconseil"],
  };

  const hasModule = (moduleName) => {
    if (!user) return false;
    if (["admin", "superadmin"].includes(user.role)) return true;
    const plan = user.subscription_plan;
    if (!plan) return false;
    return (PLAN_MODULES[plan] || []).includes(moduleName);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }} data-testid="hub-page">
      {/* Onboarding Guide */}
      <OnboardingGuide />
      {/* Video Modal */}
      {showVideo && <PresentationVideo onClose={() => setShowVideo(false)} autoPlay={true} showDownload={user?.role === "superadmin"} />}
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 py-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg" style={{ background: "#00C78F", color: "#fff" }}>C</div>
          <div>
            <p className="text-xs font-medium tracking-wide uppercase" style={{ color: '#64748b' }}>Espace CMES</p>
            <p className="text-sm font-bold" style={{ color: '#0f172a' }}>{user?.full_name || user?.pharmacy_name || user?.email}</p>
          </div>
        </div>
        <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-2 px-4 py-2 text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all" data-testid="hub-logout">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
        </div>

        {/* Plan / Trial transparency indicator */}
        {(user?.role === "pharmacy" || user?.role === "etablissement") && (myPlan?.plan || user?.subscription_plan) && (
          <PlanIndicator myPlan={myPlan} user={user} onAction={() => navigate("/choose-plan")} />
        )}

      {/* Center */}
      <div className="flex-1 flex items-center justify-center px-4 pb-10">
        <div className="max-w-6xl w-full">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-5" style={{ background: "rgba(0,199,143,0.1)", color: "#00C78F" }}>
              <Globe className="w-3.5 h-3.5" />
              Plateforme Unifiée CMES - Vision 2030
            </div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight" style={{ color: '#0f172a' }}>
              Choisissez votre <span style={{ color: "#00C78F" }}>service</span>
            </h1>
            <p className="mt-3 text-base max-w-lg mx-auto" style={{ color: '#64748b' }}>
              La suite logicielle CMES au service des pharmacies de Côte d'Ivoire
            </p>
            <button onClick={() => setShowVideo(true)} data-testid="hub-play-video-btn"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-[1.02] hover:shadow-lg"
              style={{ background: "linear-gradient(135deg, #00B4D8, #0077B6)", color: "#fff", boxShadow: "0 4px 15px rgba(0,180,216,0.3)" }}>
              <PlayCircle className="w-4 h-4" /> Decouvrir CMES en video
            </button>
          </div>

          {/* Admin quick access */}
          {(user?.role === "admin" || user?.role === "superadmin") && (
            <div className="flex justify-center gap-3 mb-6">
              <button onClick={() => navigate("/admin")} data-testid="hub-admin-link"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-cyan-600 to-teal-600 text-white hover:from-cyan-500 hover:to-teal-500 transition-all shadow-lg shadow-cyan-500/20" style={{color: '#ffffff'}}>
                <Users className="w-4 h-4" /> Supervision Agents Commerciaux & Expéditions
              </button>
              {user?.role === "superadmin" && (
                <button onClick={() => navigate("/superadmin")} data-testid="hub-sa-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-400 hover:to-orange-400 transition-all shadow-lg shadow-amber-500/20" style={{color: '#ffffff'}}>
                  <Shield className="w-4 h-4" /> Panel Super Admin
                </button>
              )}
            </div>
          )}

          {/* 3 Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* PHARMA PÉRIMÉS */}
            <ServiceCard
              onClick={() => hasModule("pharma") ? navigate("/dashboard") : null}
              testId="hub-intel-stock"
              image="https://images.unsplash.com/photo-1705517243962-230c07db6b23?w=800&q=80"
              gradient="linear-gradient(180deg, rgba(0,199,143,0.3) 0%, rgba(0,199,143,0.85) 100%)"
              icon={Shield}
              title="PHARMA PÉRIMÉS"
              subtitle="IA prédictive · Stock & destructions"
              color="#00C78F"
              badge={hasModule("pharma") ? "ACTIF" : "NON INCLUS"}
              description="Stock, destructions sécurisées, certificats PDF, analyses avancées, recommandations IA."
              features={STOCK_FEATURES}
              version="v2.0"
              locked={!hasModule("pharma")}
            />

            {/* PHARMA ASSURANCES */}
            <ServiceCard
              onClick={() => hasModule("log") ? navigate("/log") : null}
              testId="hub-cmes-log"
              image="https://images.unsplash.com/photo-1662135426498-72a27149a7dd?w=800&q=80"
              gradient="linear-gradient(180deg, rgba(56,97,251,0.3) 0%, rgba(56,97,251,0.85) 100%)"
              icon={FileText}
              title="PHARMA ASSURANCES"
              subtitle="Recouvrement & Paiements"
              color="#3B82F6"
              badge={hasModule("log") ? "ACTIF" : "NON INCLUS"}
              description="Facturation, recouvrement, créances, suivi agents commerciaux GPS, portail assurances, rapports IA."
              features={LOG_FEATURES}
              version="v2.0"
              locked={!hasModule("log")}
            />

            {/* PHARMA DISPENSATION */}
            <ServiceCard
              onClick={() => hasModule("techconseil") ? navigate("/pharmatech") : null}
              testId="hub-tech-conseil"
              image="https://images.unsplash.com/photo-1674027215032-f0c4292318ee?w=800&q=80"
              gradient="linear-gradient(180deg, rgba(139,92,246,0.3) 0%, rgba(139,92,246,0.85) 100%)"
              icon={Rocket}
              title="PHARMA DISPENSATION"
              subtitle="Copilot Officinal IA"
              color="#8B5CF6"
              badge={hasModule("techconseil") ? "ACTIF" : "NON INCLUS"}
              description="Copilot IA pharmacien, analyse d'ordonnances, Profil Patient Virtuel (Digital Twin), Google du Médicament africain, pharmacovigilance et score qualité."
              features={TECH_FEATURES}
              version="v1.0"
              locked={!hasModule("techconseil")}
            />
          </div>

          {/* Flyers & Documents Marketing - SuperAdmin only */}
          {user?.role === "superadmin" && (
            <div className="mt-8 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="flyers-section">
              <h3 className="text-sm font-bold text-slate-800 mb-1 flex items-center gap-2">
                <Download className="w-4 h-4 text-sky-500" />
                Documents Marketing — Flyers a imprimer
              </h3>
              <p className="text-[11px] text-slate-500 mb-4">Telechargez les flyers PDF professionnels pour presenter CMES aux pharmaciens</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <FlyerDownloadBtn
                  label="Flyer CMES-LOG"
                  desc="Logistique & Recouvrement"
                  color="#0ea5e9"
                  endpoint="/api/flyers/cmes-log"
                  testId="download-flyer-log"
                />
                <FlyerDownloadBtn
                  label="Flyer INTEL-STOCK"
                  desc="Stocks & Perimes"
                  color="#059669"
                  endpoint="/api/flyers/intel-stock"
                  testId="download-flyer-stock"
                />
                <FlyerDownloadBtn
                  label="Flyer TECH-CONSEIL"
                  desc="Intelligence Pharma IA"
                  color="#8b5cf6"
                  endpoint="/api/flyers/tech-conseil"
                  testId="download-flyer-techconseil"
                />
                <FlyerDownloadBtn
                  label="Presentation Combinee"
                  desc="Les 3 services CMES"
                  color="#f59e0b"
                  endpoint="/api/flyers/combined"
                  testId="download-flyer-combined"
                />
              </div>
            </div>
          )}

          {/* Security Trust Banner */}
          <div className="mt-8 rounded-2xl p-6 border" style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #e0f2fe 50%, #faf5ff 100%)", borderColor: "#d1fae5" }} data-testid="security-banner">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(16,185,129,0.12)" }}>
                <Shield className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold mb-1 flex items-center gap-2" style={{ color: "#0f172a" }}>
                  Vos donn&eacute;es sont prot&eacute;g&eacute;es
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: "rgba(16,185,129,0.15)", color: "#059669" }}>CHIFFREMENT AES ACTIF</span>
                </h3>
                <p className="text-xs leading-relaxed mb-3" style={{ color: "#475569" }}>
                  Toutes vos donn&eacute;es sensibles (factures, noms, montants) sont chiffr&eacute;es avec l'algorithme AES-256
                  avant d'&ecirc;tre stock&eacute;es. M&ecirc;me en cas d'acc&egrave;s non autoris&eacute; &agrave; la base de donn&eacute;es, vos informations 
                  restent illisibles. Chaque acc&egrave;s est journalis&eacute;.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: "#059669" }}>
                    <Lock className="w-3 h-3" />Chiffrement AES en base
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: "#0369a1" }}>
                    <Globe className="w-3 h-3" />HTTPS / TLS 1.3
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: "#7c3aed" }}>
                    <Shield className="w-3 h-3" />Isolation par pharmacie
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] font-medium" style={{ color: "#b45309" }}>
                    <Clock className="w-3 h-3" />Journalisation des acc&egrave;s
                  </div>
                </div>
                <button onClick={() => navigate("/securite")} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold transition-colors" style={{ color: "#0369a1" }} data-testid="security-learn-more">
                  En savoir plus sur notre politique de s&eacute;curit&eacute; <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-10">
            <p className="text-xs text-slate-400">
              CMES — Coaching Medical et Épanouissement Social | Tel: 07 68 12 70 00
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const FLYER_API = process.env.REACT_APP_BACKEND_URL;

function FlyerDownloadBtn({ label, desc, color, endpoint, testId }) {
  const [loading, setLoading] = useState(false);
  const download = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await axios.get(`${FLYER_API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label.replace(/\s/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { }
    finally { setLoading(false); }
  };
  return (
    <button onClick={download} disabled={loading} data-testid={testId}
      className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:shadow-md transition-all text-left group"
      style={{ borderLeftWidth: 3, borderLeftColor: color }}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color + "15" }}>
        {loading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-t-transparent" style={{ borderColor: color, borderTopColor: "transparent" }} /> : <Download className="w-4 h-4" style={{ color }} />}
      </div>
      <div>
        <p className="text-xs font-bold text-slate-800">{label}</p>
        <p className="text-[10px] text-slate-500">{desc}</p>
      </div>
    </button>
  );
}

function ServiceCard({ onClick, testId, image, gradient, icon: Icon, title, subtitle, color, badge, description, features, version, comingSoon, locked }) {
  const isDisabled = comingSoon || locked;
  return (
    <button
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      className={`group relative rounded-3xl p-1 text-left transition-all duration-300 border overflow-hidden w-full ${
        isDisabled ? "opacity-60 cursor-default" : "hover:shadow-2xl hover:border-slate-300"
      }`}
      style={{ boxShadow: `0 4px 30px ${color}12`, background: '#ffffff', borderColor: '#e2e8f0' }}
      data-testid={testId}
    >
      <div className="relative h-36 rounded-t-2xl overflow-hidden">
        <img src={image} alt={title} className={`w-full h-full object-cover ${isDisabled ? "grayscale" : "group-hover:scale-105"} transition-transform duration-500`} />
        <div className="absolute inset-0" style={{ background: locked ? "linear-gradient(180deg, rgba(100,116,139,0.3) 0%, rgba(100,116,139,0.85) 100%)" : gradient }} />
        {locked && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
              <Lock className="w-7 h-7 text-white" />
            </div>
          </div>
        )}
        <div className="absolute bottom-3 left-4 flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white leading-tight">{title}</h3>
            <p className="text-[11px] text-white/80">{subtitle}</p>
          </div>
        </div>
        <span className={`absolute top-3 right-3 px-2.5 py-0.5 text-[10px] font-bold rounded-full tracking-wider ${
          locked ? "bg-slate-500/30 text-white" : comingSoon ? "bg-violet-500/20 text-violet-300" : "bg-white/95"
        }`} style={locked ? {} : comingSoon ? {} : { color }}>
          {badge}
        </span>
      </div>

      <div className="p-4 pt-3">
        <p className="text-xs leading-relaxed mb-3" style={{ color: '#64748b' }}>{description}</p>
        <div className="grid grid-cols-2 gap-1.5 mb-4">
          {features.map((f) => (
            <div key={f.label} className="flex items-center gap-1.5 text-[11px] rounded-lg px-2 py-1.5" style={{ color: '#475569', background: 'rgba(14,165,233,0.05)' }}>
              <f.icon className="w-3 h-3 flex-shrink-0" style={{ color: locked ? "#94a3b8" : color }} />
              <span className="truncate">{f.label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          {locked ? (
            <span className="text-xs font-semibold text-slate-400">Changez de formule pour accéder</span>
          ) : comingSoon ? (
            <span className="text-xs font-semibold text-violet-400">Disponible prochainement</span>
          ) : (
            <div className="flex items-center gap-1.5 font-semibold text-sm" style={{ color }}>
              Accéder <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </div>
          )}
          {version && <span className="text-[10px] text-slate-500">{version}</span>}
        </div>
      </div>
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────
// Plan / Trial transparency indicator (Hub)
// ─────────────────────────────────────────────────────────────────
const PACK_LABEL = {
  essentiel: "Pack ESSENTIEL",
  duo: "Pack DUO",
  institution: "Pack INSTITUTION",
};

const MODULE_LABEL = {
  assurances: "Assurances",
  perimes: "Périmés",
  dispensation: "Dispensation",
};

function fmtPrice(v) {
  return new Intl.NumberFormat("fr-FR").format(v || 0);
}

function PlanIndicator({ myPlan, user, onAction }) {
  const isTrial = myPlan?.is_trial ?? user?.subscription_is_trial;
  const trialDaysRemaining = myPlan?.trial_days_remaining;
  const trialExpired = myPlan?.trial_expired;
  const TRIAL_TOTAL = 30;

  const pack = myPlan?.pack || user?.subscription_pack;
  const modules = myPlan?.modules || user?.subscription_modules || [];
  const planName = myPlan?.plan?.name || (pack ? PACK_LABEL[pack] : "Abonnement");
  const price = myPlan?.plan?.price;

  // Urgency level
  let level = "info"; // info / warning / urgent / active
  if (trialExpired) level = "urgent";
  else if (isTrial && trialDaysRemaining !== null && trialDaysRemaining !== undefined) {
    if (trialDaysRemaining <= 3) level = "urgent";
    else if (trialDaysRemaining <= 7) level = "warning";
    else level = "info";
  } else if (!isTrial) level = "active";

  const TONE = {
    info:    { bg: "linear-gradient(90deg,#ECFDF5,#F0F9FF)", border: "#A7F3D0", accent: "#047857", soft: "#D1FAE5", icon: Sparkles },
    warning: { bg: "linear-gradient(90deg,#FEF3C7,#FFFBEB)", border: "#FCD34D", accent: "#B45309", soft: "#FDE68A", icon: Clock },
    urgent:  { bg: "linear-gradient(90deg,#FEE2E2,#FEF2F2)", border: "#FCA5A5", accent: "#B91C1C", soft: "#FECACA", icon: AlertTriangle },
    active:  { bg: "linear-gradient(90deg,#ECFDF5,#F0FDF4)", border: "#86EFAC", accent: "#047857", soft: "#BBF7D0", icon: CheckCircle2 },
  };
  const tone = TONE[level];
  const Icon = tone.icon;

  const trialPct = isTrial && trialDaysRemaining !== null && trialDaysRemaining !== undefined
    ? Math.max(0, Math.min(100, ((TRIAL_TOTAL - trialDaysRemaining) / TRIAL_TOTAL) * 100))
    : 0;

  const moduleBadges = pack === "institution"
    ? ["assurances", "perimes", "dispensation"]
    : modules;

  let primaryMessage;
  let secondaryMessage;
  let cta;
  if (trialExpired) {
    primaryMessage = "Votre essai est terminé";
    secondaryMessage = "Activez votre abonnement pour conserver l'accès à votre plan.";
    cta = "Activer maintenant";
  } else if (isTrial && level === "urgent") {
    primaryMessage = `Plus que ${trialDaysRemaining} jour${trialDaysRemaining > 1 ? "s" : ""} d'essai`;
    secondaryMessage = "C'est le moment d'activer votre abonnement pour ne pas perdre vos données.";
    cta = "Activer mon abonnement";
  } else if (isTrial && level === "warning") {
    primaryMessage = `${trialDaysRemaining} jours restants sur votre essai`;
    secondaryMessage = "Vous pouvez activer ou changer de pack à tout moment.";
    cta = "Activer / Changer";
  } else if (isTrial) {
    primaryMessage = `Essai gratuit · jour ${TRIAL_TOTAL - (trialDaysRemaining ?? TRIAL_TOTAL)}/${TRIAL_TOTAL}`;
    secondaryMessage = `${trialDaysRemaining ?? "—"} jour${trialDaysRemaining > 1 ? "s" : ""} restants. Aucun engagement, sans carte bancaire.`;
    cta = "Changer de pack";
  } else {
    primaryMessage = "Abonnement actif";
    secondaryMessage = "Merci pour votre confiance. Vous pouvez ajuster vos modules à tout moment.";
    cta = "Gérer mon abonnement";
  }

  return (
    <div className="mx-6 md:mx-10 mb-3" data-testid="plan-indicator">
      <div className="rounded-2xl border px-4 py-3 md:px-5 md:py-4 flex items-center gap-3 md:gap-4"
        style={{ background: tone.bg, borderColor: tone.border }}>

        <div className="hidden sm:flex w-10 h-10 rounded-xl items-center justify-center flex-shrink-0" style={{ background: tone.soft }}>
          <Icon className="w-5 h-5" style={{ color: tone.accent }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13px] font-semibold" style={{ color: tone.accent }} data-testid="plan-indicator-primary">
              {primaryMessage}
            </p>
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ background: tone.soft, color: tone.accent }}>
              {planName}
            </span>
            {price !== undefined && price !== null && (
              <span className="text-[11px] text-slate-600 font-medium">· {fmtPrice(price)} FCFA/mois</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <p className="text-[11px] text-slate-600 truncate">{secondaryMessage}</p>
            {moduleBadges.length > 0 && pack !== "institution" && (
              <div className="flex items-center gap-1">
                {moduleBadges.map((m) => (
                  <span key={m} className="text-[9px] px-1.5 py-0.5 rounded bg-white/70 text-slate-700 font-semibold uppercase tracking-wider" data-testid={`plan-module-${m}`}>
                    {MODULE_LABEL[m] || m}
                  </span>
                ))}
              </div>
            )}
          </div>
          {isTrial && trialDaysRemaining !== null && trialDaysRemaining !== undefined && !trialExpired && (
            <div className="mt-2 h-1 rounded-full bg-white/70 overflow-hidden" data-testid="trial-progress">
              <div className="h-full rounded-full transition-all" style={{ width: `${trialPct}%`, background: tone.accent }} />
            </div>
          )}
        </div>

        <button onClick={onAction} data-testid="plan-indicator-cta"
          className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[12px] font-bold transition-all hover:scale-[1.03] flex-shrink-0"
          style={{ background: tone.accent, color: "#fff" }}>
          {cta} <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button onClick={onAction} data-testid="plan-indicator-cta-mobile"
          className="md:hidden p-2 rounded-lg flex-shrink-0" style={{ background: tone.soft, color: tone.accent }}>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
